import type { LegalEntity } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { ApiError, api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';

type Form = {
  name: string;
  shortName: string;
  legalForm: string;
  bin: string;
  address: string;
  directorName: string;
  isActive: boolean;
};

function LegalEntityDialog({ entity, onClose }: { entity: LegalEntity | 'new'; onClose: () => void }) {
  const qc = useQueryClient();
  const isNew = entity === 'new';
  const [f, setF] = useState<Form>({
    name: isNew ? '' : entity.name,
    shortName: isNew ? '' : entity.shortName,
    legalForm: isNew ? 'ТОО' : (entity.legalForm ?? ''),
    bin: isNew ? '' : (entity.bin ?? ''),
    address: isNew ? '' : (entity.address ?? ''),
    directorName: isNew ? '' : (entity.directorName ?? ''),
    isActive: isNew ? true : entity.isActive,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...f,
        legalForm: f.legalForm || null,
        bin: f.bin || null,
        address: f.address || null,
        directorName: f.directorName || null,
      };
      return isNew
        ? api('/admin/legal-entities', { body })
        : api(`/admin/legal-entities/${entity.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'legal-entities'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      toast(isNew ? 'Юрлицо добавлено' : 'Сохранено');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    save.mutate();
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новое юрлицо группы' : entity.name}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="le-form" loading={save.isPending}>
            {isNew ? 'Добавить' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <form id="le-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Полное наименование" htmlFor="le-name" error={errors.name} className="sm:col-span-2">
          <Input
            id="le-name"
            value={f.name}
            onChange={set('name')}
            placeholder="ТОО «Название»"
            invalid={!!errors.name}
          />
        </Field>
        <Field
          label="Краткое название"
          htmlFor="le-short"
          hint="Показывается в фильтрах и меню"
          error={errors.shortName}
        >
          <Input id="le-short" value={f.shortName} onChange={set('shortName')} invalid={!!errors.shortName} />
        </Field>
        <Field label="БИН" htmlFor="le-bin" error={errors.bin}>
          <Input
            id="le-bin"
            value={f.bin}
            onChange={set('bin')}
            inputMode="numeric"
            maxLength={12}
            invalid={!!errors.bin}
          />
        </Field>
        <Field label="Форма" htmlFor="le-form-type">
          <Input id="le-form-type" value={f.legalForm} onChange={set('legalForm')} />
        </Field>
        <Field label="Руководитель" htmlFor="le-dir">
          <Input id="le-dir" value={f.directorName} onChange={set('directorName')} />
        </Field>
        <Field label="Адрес" htmlFor="le-addr" className="sm:col-span-2">
          <Input id="le-addr" value={f.address} onChange={set('address')} />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          label="Работает в портале"
          description="Отключённое юрлицо не видно в фильтрах и при создании проектов"
          checked={f.isActive}
          onChange={(e) => setF((x) => ({ ...x, isActive: e.target.checked }))}
        />
        <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : save.error} />
      </form>
    </Dialog>
  );
}

export function LegalEntitiesPage() {
  const list = useQuery({
    queryKey: ['admin', 'legal-entities'],
    queryFn: () => api<LegalEntity[]>('/admin/legal-entities'),
  });
  const [editing, setEditing] = useState<LegalEntity | 'new' | null>(null);

  return (
    <>
      <Panel
        title="Юрлица группы"
        meta="проекты, договоры и права задаются по юрлицу"
        actions={
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setEditing('new')}
          >
            Добавить юрлицо
          </Button>
        }
        bodyClassName="p-0"
      >
        {list.isLoading ? (
          <div className="px-5">
            <Spinner />
          </div>
        ) : list.error ? (
          <div className="p-5">
            <ErrorBox error={list.error} />
          </div>
        ) : (
          <ul>
            {list.data?.map((le) => (
              <li
                key={le.id}
                className="flex flex-col gap-2 border-t border-line px-5 py-3.5 first:border-0 sm:flex-row sm:items-center sm:gap-4"
              >
                <button type="button" className="flex-1 text-left" onClick={() => setEditing(le)}>
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink hover:underline">{le.name}</span>
                    {!le.isActive ? <Badge tone="neutral">отключено</Badge> : null}
                  </span>
                  <span className="mt-0.5 block text-[13px] text-ink-2">
                    {le.bin ? `БИН ${le.bin}` : 'БИН не указан'}
                    {le.directorName ? `, руководитель ${le.directorName}` : ''}
                    {le.address ? `, ${le.address}` : ''}
                  </span>
                </button>
                <HistoryButton table="legal_entities" rowId={le.id} title={le.name} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {editing ? (
        <LegalEntityDialog
          key={editing === 'new' ? 'new' : editing.id}
          entity={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}
